import { useEffect, useRef, useState, type RefObject } from 'react';
import { type SolarFluxOverlayInfo } from './SolarRoofView';

type Options = {
  isLoaded: boolean;
  mapRef: RefObject<unknown>;
  rgbUnderlay?: SolarFluxOverlayInfo;
  showRgbUnderlay?: boolean;
  aboveSatelliteCeiling?: boolean;
  underlayAtEveryZoom?: boolean;
  fluxOverlay?: SolarFluxOverlayInfo;
  showFluxOverlay?: boolean;
  /** A signed image URL failed to load (usually expired); the parent should refetch fresh URLs. */
  onImageError?: (layer: LayerName) => void;
};

type LayerName = 'rgb' | 'flux';
type GroundOverlay = {
  setMap: (map: unknown) => void;
  setOpacity: (opacity: number) => void;
};

const OPACITY: Record<LayerName, number> = { rgb: 1, flux: 0.65 };

const layerIdentity = (layer: SolarFluxOverlayInfo | undefined) =>
  layer?.url
    ? JSON.stringify([
        layer.resourceKey ?? layer.url,
        layer.bounds.sw.lat,
        layer.bounds.sw.lng,
        layer.bounds.ne.lat,
        layer.bounds.ne.lng,
      ])
    : null;

/** Image identity plus the URL a failed load was retried with, if any. */
const mountKey = (
  layer: SolarFluxOverlayInfo | undefined,
  retryUrl: string | undefined
) => {
  const identity = layerIdentity(layer);
  if (!identity) return null;
  return retryUrl ? `${identity}|${retryUrl}` : identity;
};

/**
 * Both overlays are created as soon as their imagery exists — while the
 * signed URLs are fresh — and stay mounted. The layer toggles only change
 * opacity, so turning "Sun exposure" on never re-requests the analysed
 * imagery with a signature that may have expired since the page loaded.
 *
 * Returns whether the analysed imagery has actually loaded: deep zoom past
 * Google's satellite ceiling shows nothing but the map background without it.
 */
export const useSolarGroundOverlays = ({
  isLoaded,
  mapRef,
  rgbUnderlay,
  showRgbUnderlay,
  aboveSatelliteCeiling = true,
  underlayAtEveryZoom = false,
  fluxOverlay,
  showFluxOverlay,
  onImageError,
}: Options) => {
  const rgbVisible =
    !!showRgbUnderlay && (aboveSatelliteCeiling || underlayAtEveryZoom);
  const latest = useRef({
    rgbUnderlay,
    fluxOverlay,
    rgbVisible,
    showFluxOverlay,
    onImageError,
  });
  latest.current = {
    rgbUnderlay,
    fluxOverlay,
    rgbVisible,
    showFluxOverlay,
    onImageError,
  };
  const mounted = useRef<Partial<Record<LayerName, GroundOverlay>>>({});
  /** URL whose load failed and has not been superseded by a fresh one yet. */
  const [failedUrls, setFailedUrls] = useState<
    Partial<Record<LayerName, string>>
  >({});
  /** Fresh URL to remount with after a failure; refreshes alone never remount. */
  const [retryUrls, setRetryUrls] = useState<
    Partial<Record<LayerName, string>>
  >({});
  const [loadedRgbKey, setLoadedRgbKey] = useState<string | null>(null);

  const rgbKey = mountKey(rgbUnderlay, retryUrls.rgb);
  const fluxKey = mountKey(fluxOverlay, retryUrls.flux);

  const rgbUrl = rgbUnderlay?.url;
  const fluxUrl = fluxOverlay?.url;
  useEffect(() => {
    const fresh: Partial<Record<LayerName, string | undefined>> = {
      rgb: rgbUrl,
      flux: fluxUrl,
    };
    const ready = (Object.keys(failedUrls) as LayerName[]).filter(
      (name) => fresh[name] && fresh[name] !== failedUrls[name]
    );
    if (!ready.length) return;
    setRetryUrls((prev) => {
      const next = { ...prev };
      ready.forEach((name) => (next[name] = fresh[name]));
      return next;
    });
    setFailedUrls((prev) => {
      const next = { ...prev };
      ready.forEach((name) => delete next[name]);
      return next;
    });
  }, [rgbUrl, fluxUrl, failedUrls]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    const map = mapRef.current;
    const created: GroundOverlay[] = [];
    const probes: HTMLImageElement[] = [];
    const visible = (name: LayerName) =>
      name === 'rgb'
        ? latest.current.rgbVisible
        : !!latest.current.showFluxOverlay;
    const mount = (
      name: LayerName,
      layer: SolarFluxOverlayInfo | undefined,
      key: string | null
    ) => {
      if (!layer?.url || !key) return;
      const url = layer.url;
      const overlay: GroundOverlay = new window.google.maps.GroundOverlay(
        url,
        new window.google.maps.LatLngBounds(layer.bounds.sw, layer.bounds.ne),
        {
          map,
          opacity: visible(name) ? OPACITY[name] : 0,
          clickable: false,
        }
      );
      created.push(overlay);
      mounted.current[name] = overlay;
      // GroundOverlay has no load/error events; a probe of the same URL
      // (served from the image cache) reports whether the imagery arrived.
      const probe = new Image();
      probe.onload = () => {
        if (name === 'rgb') setLoadedRgbKey(key);
      };
      probe.onerror = () => {
        setFailedUrls((prev) =>
          prev[name] === url ? prev : { ...prev, [name]: url }
        );
        latest.current.onImageError?.(name);
      };
      probe.src = url;
      probes.push(probe);
    };
    // Google stacks overlays in mount order: the opaque analysed imagery goes
    // first so it never covers the irradiance colours.
    mount('rgb', latest.current.rgbUnderlay, rgbKey);
    mount('flux', latest.current.fluxOverlay, fluxKey);
    return () => {
      probes.forEach((probe) => {
        probe.onload = null;
        probe.onerror = null;
      });
      created.forEach((overlay) => overlay.setMap(null));
      mounted.current = {};
    };
  }, [isLoaded, mapRef, rgbKey, fluxKey]);

  useEffect(() => {
    mounted.current.rgb?.setOpacity(rgbVisible ? OPACITY.rgb : 0);
  }, [rgbVisible, rgbKey, fluxKey]);

  useEffect(() => {
    mounted.current.flux?.setOpacity(showFluxOverlay ? OPACITY.flux : 0);
  }, [showFluxOverlay, rgbKey, fluxKey]);

  return { rgbReady: rgbKey != null && loadedRgbKey === rgbKey };
};
